import React, { useCallback, useEffect, useState } from "react";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import axios from "../utils/axios";
import "./SessionParticipants.css";
import logger from "../utils/logger";
import { UserDto } from "../models/user-dto";
import { generateDistinctColorOptimized } from "../utils/colors";

interface SessionParticipantsProps {
  connection: HubConnection | null;
  connectionState: HubConnectionState;
  sessionId: number;
  updatingUsers: string[];
  isPuzzleComplete: boolean;
}

const SessionParticipants: React.FC<SessionParticipantsProps> = ({
  connection,
  connectionState,
  sessionId,
  updatingUsers,
  isPuzzleComplete,
}) => {
  const [users, setUsers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Function to fetch user details that aren't already in our dictionary
  const fetchMissingUsers = useCallback(
    async () => {
      try {
        setLoading(true);
        const response = await axios.get<UserDto[]>(
          `/api/cross/session/${sessionId}/users`
        );

        setUsers(response.data);
      } catch (error) {
        logger.error("Error fetching user details:", error);
      } finally {
        setLoading(false);
      }
    },
    [sessionId]
  );

  // Listen for user join/leave events
  useEffect(() => {
    if (!connection || connectionState !== HubConnectionState.Connected) return;

    // Handle when a user joins
    const handleUserJoined = (data: { joinee: string; members: string[] }) => {
      logger.debug(`User ${data.joinee} joined. Current members:`, data.members);

      // Get all user data we might be missing
      fetchMissingUsers();
    };

    // Handle when a user leaves
    const handleUserLeft = (data: { leaver: string; members: string[] }) => {
      logger.debug(`User ${data.leaver} left. Current members:`, data.members);

      // Get all user data we might be missing
      fetchMissingUsers();
    };

    connection.on("UserJoined", handleUserJoined);
    connection.on("UserLeft", handleUserLeft);

    return () => {
      connection.off("UserJoined", handleUserJoined);
      connection.off("UserLeft", handleUserLeft);
    };
  }, [connection, connectionState, fetchMissingUsers]);

  // Get the initial participant list - no specific event for this
  // so we just fetch when the component mounts
  useEffect(() => {
    const fetchInitialParticipants = async () => {
      if (!sessionId || sessionId === 0) return;
      try {
        fetchMissingUsers();
      } catch (error) {
        logger.error("Error fetching initial participants:", error);
      }
    };

    fetchInitialParticipants();
  }, [sessionId, fetchMissingUsers]);

  return (
    <div className="session-participants">
      <div className="participants-list">
        {Object.values(users).map((user) => (
          <div
            key={user.email}
            className={`participant-circle ${updatingUsers.includes(user.userKey) ? "updating" : ""}`}
            style={{
              backgroundColor:  generateDistinctColorOptimized( user.userKey, users.length,isPuzzleComplete || user.isActive),
            }}
            title={user.username || user.email}
          >
            {user.initials || user.email.substring(0, 2).toUpperCase()}
          </div>
        ))}
        {loading && <div className="participant-circle loading">...</div>}
      </div>
    </div>
  );
};

export default SessionParticipants;
